import {
  initializeCampaignVariant,
  updateCampaignVariant,
} from 'actions/campaignVariants'
import noPushIcon from 'assets/images/campaign-center/builder/img_noPush.svg'
import pushIcon from 'assets/images/campaign-center/builder/img_push.svg'
import EmptyContentSection from 'components/review/EmptyContentSection'
import { buildTranslate } from 'locales'
import { Fields as Campaign } from 'models/Campaign'
import { VariantOrCampaign } from 'models/CampaignVariant'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useBuilderReadOnly } from 'scenes/CampaignCenter/Builder/CampaignConfigContext'
import PushPreview from 'scenes/CampaignCenter/Builder/components/Previews/Push'
import Channel from './Channel'
import Edit from './Options/Edit'
import Remove from './Options/Remove'
import RemoveModal from './RemoveModal'

const t = buildTranslate('thanx_campaigns.builder.steps.setup.messaging.push')

type PropsT = {
  campaign: Campaign
  config: VariantOrCampaign
}

const PushChannel: React.FC<PropsT> = ({ campaign, config }) => {
  const [removeModalOpen, setRemoveModalOpen] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const dispatch = useDispatch()
  const readOnly = useBuilderReadOnly()

  async function handleAdd() {
    setIsAdding(true)
    await dispatch(
      initializeCampaignVariant(campaign.id, config.id, { channel: 'push' })
    )
    setIsAdding(false)

    dispatch(push(`/thanx_campaigns/${campaign.id}/push/${config.id}`))
  }

  async function handleEdit() {
    setIsAdding(true)
    setIsAdding(false)

    dispatch(push(`/thanx_campaigns/${campaign.id}/push/${config.id}`))
  }

  async function handleRemovePrompt() {
    setRemoveModalOpen(true)
  }

  async function handleRemove() {
    setIsRemoving(true)
    await dispatch(
      updateCampaignVariant(campaign.id, config.id, {
        ...config,
        notify_push: false,
      })
    )
    setIsRemoving(false)
    setRemoveModalOpen(false)
  }

  // Fall back to the legacy field while responses without the capability
  // field can still be in flight (pre-#983 backend or a backend rollback).
  const nonRewardPushAllowed =
    campaign.non_reward_push_allowed ?? campaign.allow_non_reward_messages
  const requiredRewardMissing = !nonRewardPushAllowed && !config.redeem

  if (!config.notify_push) {
    return (
      <EmptyContentSection
        icon={noPushIcon}
        isEditable={
          config.internal_type !== 'control' &&
          !requiredRewardMissing &&
          !readOnly
        }
        isAdding={isAdding}
        onAdd={handleAdd}
        addText={t('add')}
        text={t(requiredRewardMissing ? 'missing_reward' : 'missing')}
      />
    )
  }

  return (
    <>
      <Channel
        title={t('title')}
        icon={pushIcon}
        actions={
          <div className="d-flex">
            {!readOnly && (
              <Remove
                title={t('remove_modal.title')}
                onRemove={handleRemovePrompt}
              />
            )}
            <Edit title={t('edit')} onEdit={handleEdit} disabled={readOnly} />
          </div>
        }
      >
        <PushPreview
          text={config.notify_push_text}
          title={config.notify_push_title}
          subtitle={config.notify_push_subtitle}
          imageUrl={config.notify_push_hero_image_urls?.default}
        />
      </Channel>
      <RemoveModal
        onRemove={handleRemove}
        isRemoving={isRemoving}
        campaign={campaign}
        variant={config}
        channel="push"
        isOpen={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
      />
    </>
  )
}

export default PushChannel
